import React, { useState, useEffect, ChangeEvent } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { RATINGS } from 'utils/constants';
import Api from 'api';
import { FeedbackModalJob, JobLocation } from 'components/FeedbackModal';
import SuccessModal from './components/SuccessModal';
import { createGlobalStyle } from 'styled-components';
import { exitReact } from 'utils'
import { 
  calculateAverageRating, 
  mapRatingToConstant, 
  formatFeedbackDetails,
  isFeedbackFormValid 
} from 'utils/feedbackHelpers';

// Icons
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HandshakeIcon from '@mui/icons-material/Handshake';

// Import styles from the styles file
import {
  PageContainer,
  Header,
  PageTitle,
  ContentSection,
  ThankYouText,
  OfficeInfo,
  Description,
  RatingGroup,
  RatingTitle,
  IconWrapper,
  RatingTitleText,
  RatingSubtitle,
  StarsContainer,
  Star,
  FeedbackInput,
  FutureCollabContainer,
  FutureCollabTitle,
  EmojiContainer,
  EmojiOption,
  Emoji,
  EmojiLabel,
  ButtonsContainer,
  PrimaryButton,
  SecondaryButton
} from './FeedbackPage.styles';

// Global style to ensure modals display above all other content
const GlobalStyle = createGlobalStyle`
  .MuiDialog-root {
    z-index: 9999 !important;
  }
  
  .MuiBackdrop-root {
    z-index: 9998 !important;
  }
`;

interface RatingState extends Record<string, number | null> {
  friendly: number | null;
  infection: number | null;
  tools: number | null;
  setup: number | null;
}

interface FeedbackState extends Record<string, string> {
  friendly: string;
  infection: string;
  tools: string;
  additional: string;
  setup: string;
}

type FutureCollaboration = 'love' | 'fine' | 'prefer-not' | null;

export default function FeedbackPage() {
  const history = useHistory();
  const location = useLocation();
  const [job, setJob] = useState<FeedbackModalJob | null>(null);
  const [ratings, setRatings] = useState<RatingState>({
    friendly: null,
    infection: null,
    tools: null,
    setup: null
  });
  const [feedback, setFeedback] = useState<FeedbackState>({
    friendly: '',
    infection: '',
    tools: '',
    setup: '',
    additional: ''
  });
  const [futureCollab, setFutureCollab] = useState<FutureCollaboration>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [hasAnyInput, setHasAnyInput] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    let jobData: FeedbackModalJob | null = null;
    let foundJob = false;

    // 1. Try location state first
    if (location.state && typeof location.state === 'object' && 'job' in location.state) {
      jobData = location.state.job as FeedbackModalJob;
      foundJob = true;
      // console.log('[FeedbackPage] Loaded job from location.state');
    }

    // 2. If not in state, try localStorage (from TempStars.App.goReact)
    if (!foundJob) {
      try {
        const pageDataString = localStorage.getItem('pageData');
        if (pageDataString) {
          const pageData = JSON.parse(pageDataString);
          if (pageData && pageData.page === 'hygienistFeedback' && pageData.job) {
            jobData = pageData.job as FeedbackModalJob;
            foundJob = true;
            // console.log('[FeedbackPage] Loaded job from localStorage');
            // Clean up localStorage immediately after reading
            localStorage.removeItem('pageData'); 
          }
        }
      } catch (error) {
        console.error('Error reading job data from localStorage:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('pageData'); 
      }
    }

    if (foundJob && jobData) {
      setJob(jobData);
    } else {
      // console.error('[FeedbackPage] No job data found, navigating back.');
      history.goBack();
    }
  }, [location, history]);

  // Check if form is valid when ratings or futureCollab changes
  useEffect(() => {
    setFormValid(isFeedbackFormValid(ratings, futureCollab));
  }, [ratings, futureCollab]);

  // Check if any fields have been filled
  useEffect(() => {
    const hasRating = Object.values(ratings).some(rating => rating !== null);
    const hasText = Object.values(feedback).some(text => text.trim() !== '');
    const hasCollabChoice = futureCollab !== null;
    
    setHasAnyInput(hasRating || hasText || hasCollabChoice);
  }, [ratings, feedback, futureCollab]);

  const handleBack = () => {
    history.push('/loading');
    exitReact('home');
  };

  const handleRatingChange = (category: keyof RatingState, value: number) => {
    setRatings(prev => {
      // If clicking the same star again, unselect it
      if (prev[category] === value) {
        return { ...prev, [category]: null };
      }
      // Otherwise set the rating
      return { ...prev, [category]: value };
    });
  };

  const handleFeedbackChange = (category: keyof FeedbackState, value: string) => {
    setFeedback(prev => ({ ...prev, [category]: value }));
  };

  const handleEmojiSelect = (value: FutureCollaboration) => {
    setFutureCollab(value);
  };

  const handleSubmit = async () => {
    if (!job || !formValid) return;
    
    setIsSubmitting(true);
    try {
      // The job object might not have locationId directly, so we need to get it from location
      // and ensure it's a number
      const locationId = job.location && job.location.id 
        ? Number(job.location.id) 
        : job.locationId 
          ? Number(job.locationId) 
          : 0;

      // Create the review data object with proper type handling
      const reviewData = {
        jobId: Number(job.id),  // Ensure jobId is a number
        locationId,  // Use extracted locationId
        friendlinessRating: ratings.friendly ? Number(ratings.friendly) : 0,
        friendlinessComments: feedback.friendly || '',
        infectionControlRating: ratings.infection ? Number(ratings.infection) : 0,
        infectionControlComments: feedback.infection || '',
        equipmentRating: ratings.tools ? Number(ratings.tools) : 0,
        equipmentComments: feedback.tools || '',
        preparationRating: ratings.setup ? Number(ratings.setup) : 0,
        preparationComments: feedback.setup || '',
        additionalFeedback: feedback.additional || '',
        collaborationPreference: futureCollab === 'love' ? 1 : futureCollab === 'fine' ? 2 : 3 as 1 | 2 | 3
      };
      
      // Submit the review using the new API endpoint
      await Api.submitTalentReview(reviewData);
      
      // Mark form as submitted
      setFormSubmitted(true);
      
      // Show success modal instead of alert
      setShowSuccessModal(true);
    } catch (error) {
      // console.error("Error submitting feedback:", error);
      alert('Error saving feedback. Please try again.');
      // Use new exit logic on error too?
      (window as any).TempStars.App.exitReactStyles(() => { (window as any).TempStars.Hygienist.Router.goForwardPage('home'); });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismissWithoutFeedback = async () => {
    if (!job) return;
    setIsSubmitting(true);
    try {
      // Use the dismiss review API endpoint with proper type conversion
      await Api.dismissTalentReview(Number(job.id));
      
      // Mark form as submitted
      setFormSubmitted(true);
      
      // Navigate directly to home using TempStars global
      (window as any).TempStars.App.exitReactStyles(() => { (window as any).TempStars.Hygienist.Router.goForwardPage('home'); });
    } catch (error) {
      // console.error("Error dismissing feedback:", error);
      alert('Error. Please try again.');
      // Use new exit logic on error too?
      (window as any).TempStars.App.exitReactStyles(() => { (window as any).TempStars.Hygienist.Router.goForwardPage('home'); });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close and navigation to home calendar
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Navigate to home calendar page using TempStars global
    (window as any).TempStars.App.exitReactStyles(() => { (window as any).TempStars.Hygienist.Router.goForwardPage('home'); });
  };

  const handleSkipForNow = () => {
    // Remove this functionality as it's being replaced by server-side tracking
    // Just go back without saving anything
    history.goBack();
  };

  const isUS = () => {
    // This is a placeholder - you'll need to implement logic to determine if user is in US
    return false; // Default to CA for now
  };

  if (!job) {
    return null;
  }

  return (
    <PageContainer>
      <GlobalStyle />
      <Header>
        <IconButton onClick={handleBack} edge="start" style={{color: 'white'}}>
          <ArrowBackIcon />
        </IconButton>
        <PageTitle>Please Review Your Shift</PageTitle>
        <div style={{ width: 48 }}></div> {/* Empty div for balance */}
      </Header>

      <ContentSection>
        <ThankYouText>
          Nice work completing a shift!
        </ThankYouText>
        
        <Description>
          Please Rate Your Experience with {job?.location.practiceName} on { moment(job?.shifts[0]?.shiftDate)?.format('MMMM D')}
        </Description>
        
        <Description>
          Your feedback is confidential and will only be used internally by TempStars to improve matching
          quality. Honest, constructive feedback helps us ensure the best possible matches for dental offices
          and professionals. Your comments will not be displayed publicly.
        </Description>
        
        {/* Friendly & Professional */}
        <RatingGroup>
          <RatingTitle>
            <IconWrapper>
              <GroupIcon />
            </IconWrapper>
            <RatingTitleText>Friendly & Professional</RatingTitleText>
          </RatingTitle>
          <RatingSubtitle>
            Was the team overall friendly and professional in their communication and overall demeanor?
          </RatingSubtitle>
          <StarsContainer>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={`friendly-${star}`}
                filled={ratings.friendly !== null && star <= ratings.friendly}
                onClick={() => handleRatingChange('friendly', star)}
              >
                {ratings.friendly !== null && star <= ratings.friendly ? '★' : '☆'}
              </Star>
            ))}
          </StarsContainer>
          <FeedbackInput
            placeholder="Please provide details if any"
            rows={3}
            value={feedback.friendly}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFeedbackChange('friendly', e.target.value)}
          />
        </RatingGroup>
        
        {/* Infection Control */}
        <RatingGroup>
          <RatingTitle>
            <IconWrapper>
              <SearchIcon />
            </IconWrapper>
            <RatingTitleText>Infection Control and Workplace Compliance</RatingTitleText>
          </RatingTitle>
          <RatingSubtitle>
            Was the office compliant with PPE, infection control, and workplace safety standards?
          </RatingSubtitle>
          <StarsContainer>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={`infection-${star}`}
                filled={ratings.infection !== null && star <= ratings.infection}
                onClick={() => handleRatingChange('infection', star)}
              >
                {ratings.infection !== null && star <= ratings.infection ? '★' : '☆'}
              </Star>
            ))}
          </StarsContainer>
          <FeedbackInput
            placeholder="Please provide details if any"
            rows={3}
            value={feedback.infection}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFeedbackChange('infection', e.target.value)}
          />
        </RatingGroup>
        
        {/* Tools & Equipment */}
        <RatingGroup>
          <RatingTitle>
            <IconWrapper>
              <BuildIcon />
            </IconWrapper>
            <RatingTitleText>Tools & Equipment</RatingTitleText>
          </RatingTitle>
          <RatingSubtitle>
            Were the tools and equipment adequate to perform your duties in a professional manner?
          </RatingSubtitle>
          <StarsContainer>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={`tools-${star}`}
                filled={ratings.tools !== null && star <= ratings.tools}
                onClick={() => handleRatingChange('tools', star)}
              >
                {ratings.tools !== null && star <= ratings.tools ? '★' : '☆'}
              </Star>
            ))}
          </StarsContainer>
          <FeedbackInput
            placeholder="Please provide details if any"
            rows={3}
            value={feedback.tools}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFeedbackChange('tools', e.target.value)}
          />
        </RatingGroup>
        
        {/* Preparation and Set Up */}
        <RatingGroup>
          <RatingTitle>
            <IconWrapper>
              <AssignmentIcon />
            </IconWrapper>
            <RatingTitleText>Preparation and Set Up For Success</RatingTitleText>
          </RatingTitle>
          <RatingSubtitle>
            Was the office organized to set you up for success – was it easy to find and park, was there adequate preparation time, orientation, etc. to start the day off the right way and have a smooth shift?
          </RatingSubtitle>
          <StarsContainer>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={`setup-${star}`}
                filled={ratings.setup !== null && star <= ratings.setup}
                onClick={() => handleRatingChange('setup', star)}
              >
                {ratings.setup !== null && star <= ratings.setup ? '★' : '☆'}
              </Star>
            ))}
          </StarsContainer>
          <FeedbackInput
            placeholder="Please provide details if any"
            rows={3}
            value={feedback.setup}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFeedbackChange('setup', e.target.value)}
          />
        </RatingGroup>
        
        {/* Future Collaboration */}
        <FutureCollabContainer>
          <FutureCollabTitle>
            <IconWrapper>
              <HandshakeIcon />
            </IconWrapper>
            <RatingTitleText>Future Collaboration</RatingTitleText>
          </FutureCollabTitle>
          <EmojiContainer>
            <EmojiOption 
              selected={futureCollab === 'love'}
              onClick={() => handleEmojiSelect('love')}
            >
              <Emoji>😊</Emoji>
              <EmojiLabel>I would love to work at this office again</EmojiLabel>
            </EmojiOption>
            <EmojiOption
              selected={futureCollab === 'fine'}
              onClick={() => handleEmojiSelect('fine')}
            >
              <Emoji>😐</Emoji>
              <EmojiLabel>The office was fine but not a favorite</EmojiLabel>
            </EmojiOption>
            <EmojiOption
              selected={futureCollab === 'prefer-not'}
              onClick={() => handleEmojiSelect('prefer-not')}
            >
              <Emoji>😞</Emoji>
              <EmojiLabel>I prefer not to work at this office again</EmojiLabel>
            </EmojiOption>
          </EmojiContainer>
          <FeedbackInput
            placeholder="Additional feedback if any"
            rows={3}
            value={feedback.additional}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleFeedbackChange('additional', e.target.value)}
          />
        </FutureCollabContainer>
        
        <ButtonsContainer>
          <PrimaryButton 
            onClick={hasAnyInput ? handleSubmit : handleDismissWithoutFeedback} 
            disabled={isSubmitting || formSubmitted || (hasAnyInput && !formValid)}
          >
            {hasAnyInput ? 'Submit Feedback' : 'Dismiss without feedback'}
          </PrimaryButton>
        </ButtonsContainer>
      </ContentSection>

      {/* Add the Success Modal */}
      <SuccessModal 
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
      />
    </PageContainer>
  );
};
